import {useEffect, useState, useContext, useRef, useCallback} from 'react'
import {WalletContext} from '../../components/WalletContext'
import {tonConnectUI} from '../../tonConnectUI'
import './Mint.css'

export default function Mint() {
    const [selectedFile, setSelectedFile] = useState(null)
    const {walletAddress} = useContext(WalletContext)
    const [nftName, setNftName] = useState('')
    const [nftDescription, setNftDescription] = useState('')
    const [loading, setLoading] = useState(false)
    const [status, setStatus] = useState('')
    const [result, setResult] = useState(null)

    // Camera state
    const [showCamera, setShowCamera] = useState(false)
    const [facingMode, setFacingMode] = useState('environment')
    const [devices, setDevices] = useState([])
    const [deviceIndex, setDeviceIndex] = useState(0)
    const [torchAvail, setTorchAvail] = useState(false)
    const [torchOn, setTorchOn] = useState(false)
    const [reviewBlobUrl, setReviewBlobUrl] = useState(null) // preview before use
    const videoRef = useRef(null)
    const streamRef = useRef(null)

    // ===== Helpers =====
    const listCameras = async () => {
        const all = await navigator.mediaDevices.enumerateDevices()
        const cams = all.filter(d => d.kind === 'videoinput')
        setDevices(cams)
    }

    const startStream = useCallback(
        async modeOrDeviceId => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(t => t.stop())
                streamRef.current = null
            }

            const constraints =
                typeof modeOrDeviceId === 'string' &&
                modeOrDeviceId.startsWith('device:')
                    ? {
                        video: {
                            deviceId: {exact: modeOrDeviceId.replace('device:', '')},
                        },
                        audio: false,
                    }
                    : {video: {facingMode: {ideal: modeOrDeviceId}}, audio: false}

            const stream = await navigator.mediaDevices.getUserMedia(constraints)
            streamRef.current = stream
            const video = videoRef.current
            if (video) {
                video.srcObject = stream
                await video.play()
                const track = stream.getVideoTracks()[0]
                const caps = track.getCapabilities?.() || {}
                setTorchAvail(Boolean(caps.torch))
                setTorchOn(false)
                video.style.transform =
                    facingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)'
            }
        },
        [facingMode]
    )

    const applyTorch = async on => {
        try {
            const track = streamRef.current?.getVideoTracks?.()[0]
            if (!track) return
            await track.applyConstraints({advanced: [{torch: on}]})
            setTorchOn(on)
        } catch {
            // ignore
        }
    }

    const openCamera = async () => {
        try {
            await listCameras()
            setShowCamera(true)
            await startStream(facingMode)
            document.body.style.overflow = 'hidden' // prevent background scroll
            setStatus('')
        } catch (err) {
            setShowCamera(false)
            setStatus('Failed to open camera. Allow access or use file upload.')
            console.error(err)
        }
    }

    const closeCamera = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop())
            streamRef.current = null
        }
        setTorchOn(false)
        setTorchAvail(false)
        setReviewBlobUrl(null)
        setShowCamera(false)
        document.body.style.overflow = ''
    }

    const flipCamera = useCallback(async () => {
        if (devices.length > 1) {
            const next = (deviceIndex + 1) % devices.length
            setDeviceIndex(next)
            await startStream('device:' + devices[next].deviceId)
        } else {
            const nextMode = facingMode === 'environment' ? 'user' : 'environment'
            setFacingMode(nextMode)
            await startStream(nextMode)
        }
    }, [devices, deviceIndex, facingMode, startStream])

    const capturePhoto = useCallback(() => {
        const video = videoRef.current
        if (!video) return
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth || 1280
        canvas.height = video.videoHeight || 720
        const ctx = canvas.getContext('2d')

        if (facingMode === 'user') {
            ctx.translate(canvas.width, 0)
            ctx.scale(-1, 1)
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)

        canvas.toBlob(
            blob => {
                if (!blob) return
                if (reviewBlobUrl) URL.revokeObjectURL(reviewBlobUrl)
                setReviewBlobUrl(URL.createObjectURL(blob))
            },
            'image/jpeg',
            0.92
        )
    }, [facingMode, reviewBlobUrl])

    const confirmCaptured = useCallback(async () => {
        if (!reviewBlobUrl) return
        const res = await fetch(reviewBlobUrl)
        const blob = await res.blob()
        const file = new File([blob], `camera_${Date.now()}.jpg`, {
            type: 'image/jpeg',
        })
        setSelectedFile(file)
        closeCamera()
    }, [reviewBlobUrl])

    const handleFileChange = e => {
        const file = e.target.files[0]
        setSelectedFile(file || null)
    }

    const handleNameChange = e => {
        const value = e.target.value
        const formatted = value.charAt(0).toUpperCase() + value.slice(1)
        setNftName(formatted)
    }
    const isValidName = nftName.length > 0 && /^[A-Z]/.test(nftName)

    function fileToBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result)
            reader.onerror = error => reject(error)
            reader.readAsDataURL(file)
        })
    }

    const handleMint = async () => {
        if (!selectedFile || !walletAddress || !isValidName) return
        setLoading(true)
        setStatus('Uploading image and preparing transaction...')
        setResult(null)

        try {
            const photo = await fileToBase64(selectedFile)
            const timestamp = new Date().toISOString()
            const requestBody = {
                photo,
                owner_address: walletAddress,
                collection_address: 'kQC2OsGzZk8OEgXCTJJA0w-wHcaX9BdixRTQfgIXi7ungPli',
                metadata: {
                    name: nftName,
                    description: `${nftDescription} Minted by ${walletAddress.slice(
                        0,
                        8
                    )}...${walletAddress.slice(-4)} on Fucken.Me`,
                    attributes: [
                        {trait_type: 'Minted By', value: walletAddress},
                        {trait_type: 'Mint Date', value: timestamp},
                        {trait_type: 'Platform', value: 'Fucken.Me!'},
                        {trait_type: 'Collection', value: 'Fucken.Me!'},
                    ],
                },
            }

            const res = await fetch('https://mint.fucken.me/item/mint', {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify(requestBody),
            })

            const raw = await res.text()
            if (!res.ok) throw new Error('Backend error: ' + raw)
            let data
            try {
                data = JSON.parse(raw)
            } catch (e) {
                throw new Error('Response is not JSON: ' + e.message)
            }
            if (!data.transaction)
                throw new Error('No transaction in backend response')

            setResult(data)
            setStatus('NFT minted successfully!')
        } catch (err) {
            console.error('Mint error:', err)
            setStatus('Error: ' + err.message)
        } finally {
            setLoading(false)
        }
    }

    const handleSignAndSend = async transaction => {
        try {
            await tonConnectUI.sendTransaction(transaction)
            setStatus('Transaction sent to wallet!')
        } catch (err) {
            console.error('Send transaction error:', err)
            setStatus('Send error')
        }
    }
    useEffect(() => {
        if (result?.transaction) handleSignAndSend(result.transaction)
    }, [result])

    // keyboard shortcuts inside camera
    useEffect(() => {
        if (!showCamera) return
        const onKey = e => {
            if (e.key === 'Escape') {
                e.preventDefault()
                closeCamera()
            }
            if (e.key.toLowerCase() === 'f') {
                e.preventDefault()
                flipCamera()
            }
            if (e.code === 'Space') {
                e.preventDefault()
                reviewBlobUrl ? confirmCaptured() : capturePhoto()
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    }, [
        showCamera,
        reviewBlobUrl,
        devices,
        deviceIndex,
        facingMode,
        capturePhoto,
        confirmCaptured,
        flipCamera,
    ])

    return (
        <>
            {!walletAddress && (
                <div className='preview-box'>
                    <p className='preview-label' style={{color: '#ff4444'}}>
                        🚫 Please connect your wallet to mint an NFT
                    </p>
                </div>
            )}

            {walletAddress && (
                <div className='hero'>
                    <div className='hero-text'>
                        <h1>Mint your NFT</h1>
                        <p>Take a picture or upload an image to mint on TON.</p>

                        {!selectedFile ? (
                            <>
                                <button className='mint-button' onClick={openCamera}>
                                    📸 Open Camera
                                </button>
                                <label className='mint-button'>
                                    🗂️ Upload / Gallery
                                    <input
                                        type='file'
                                        accept='image/*'
                                        style={{display: 'none'}}
                                        onChange={handleFileChange}
                                        onClick={e => (e.target.value = null)}
                                    />
                                </label>
                            </>
                        ) : (
                            <div className='preview-box'>
                                <div className='preview-header'>
                                    <p className='preview-label'>
                                        ✅ Selected:
                                        <br/>
                                        {selectedFile.name}
                                    </p>
                                    <button
                                        className='remove-button'
                                        onClick={() => setSelectedFile(null)}
                                        disabled={loading}
                                        aria-label='Remove selected image'
                                    >
                                        ✖
                                    </button>
                                </div>

                                <div className='mint-form'>
                                    {status && (
                                        <div
                                            style={{
                                                marginTop: 16,
                                                color: status.startsWith('Error')
                                                    ? '#ff4444'
                                                    : '#44cc77',
                                            }}
                                        >
                                            {status}
                                        </div>
                                    )}

                                    <img
                                        src={URL.createObjectURL(selectedFile)}
                                        alt='Selected preview'
                                        className='preview-image'
                                    />

                                    <input
                                        type='text'
                                        placeholder='Enter NFT name'
                                        className='nft-name-input'
                                        value={nftName}
                                        onChange={handleNameChange}
                                        disabled={loading}
                                    />
                                    <textarea
                                        placeholder='Enter NFT description'
                                        className='nft-desc-input'
                                        value={nftDescription}
                                        onChange={e => setNftDescription(e.target.value)}
                                        rows={3}
                                        maxLength={300}
                                        disabled={loading}
                                    />

                                    <button
                                        className='mint-button mint-now'
                                        disabled={!isValidName || loading}
                                        onClick={handleMint}
                                    >
                                        {loading ? '⏳ Minting...' : '🚀 Mint now'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* ===== Camera modal ===== */}
                    {showCamera && (
                        <div className='camera-modal' role='dialog' aria-modal='true'>
                            <div className='camera-frame'>
                                <div className='camera-header'>
                                    <button className='icon-btn' onClick={closeCamera}>
                                        ✖ Close
                                    </button>
                                    <div className='camera-title'>
                                        {reviewBlobUrl
                                            ? 'Review'
                                            : facingMode === 'user'
                                                ? 'Front camera'
                                                : 'Back camera'}
                                    </div>
                                    <div className='camera-controls'>
                                        <button
                                            className='icon-btn'
                                            onClick={flipCamera}
                                            title='Switch camera (F)'
                                        >
                                            🔁 Flip
                                        </button>
                                        <button
                                            className='icon-btn'
                                            disabled={!torchAvail}
                                            onClick={() => applyTorch(!torchOn)}
                                            title='Toggle torch'
                                        >
                                            {torchOn ? '🔦 On' : '🔦 Off'}
                                        </button>
                                    </div>
                                </div>

                                <div className='camera-viewport' onDoubleClick={flipCamera}>
                                    {!reviewBlobUrl ? (
                                        <>
                                            <video
                                                ref={videoRef}
                                                className='camera-video'
                                                playsInline
                                                autoPlay
                                                muted
                                            />
                                            <div className='camera-overlay'>
                                                <div className='overlay-guides'></div>
                                            </div>
                                        </>
                                    ) : (
                                        <img
                                            src={reviewBlobUrl}
                                            alt='Captured'
                                            className='camera-video'
                                        />
                                    )}
                                </div>

                                <div className='camera-footer'>
                                    {!reviewBlobUrl ? (
                                        <>
                                            <div className='controls-row'>
                                                <button
                                                    className='shutter'
                                                    onClick={capturePhoto}
                                                    aria-label='Capture photo'
                                                ></button>
                                            </div>
                                            <div className='helper'>
                                                Space — capture · F — flip · Esc — close
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className='review-strip'>
                                                <button
                                                    className='review-btn'
                                                    onClick={() => setReviewBlobUrl(null)}
                                                >
                                                    ↩️ Retake
                                                </button>
                                                <button
                                                    className='review-btn primary'
                                                    onClick={confirmCaptured}
                                                >
                                                    ✅ Use photo
                                                </button>
                                            </div>
                                            <div className='helper'>
                                                Looks good? You can retake or use this shot.
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    )
}
